import { useMemo } from 'react';

import { useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { faCalculatorSimple } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Box, TableBody, TableHead, TableRow, Typography } from '@mui/material';

import { calculationQueries } from '@repo/api/claim/calculations';
import { isCalculatedCollectible } from '@repo/api/claim/calculations.types';
import { claimQueries } from '@repo/api/claim/claims';
import { questionnaireQueries } from '@repo/api/claim/questionnaire';
import { countryQueries } from '@repo/api/countries';
import useCurrency from '@repo/features/hooks/useCurrency';

import { CalculationTable } from './-components/CalculationTable';
import { Cell } from './-components/Cell';
import { CurrencyCell } from './-components/CurrencyCell';
import { Footnote } from './-components/Footnote';

type FootnoteData = {
	letter: string;
	note: string;
};

const bankFeeDescriptions = new Set([
	'Bank Fees',
	'Bank Fees and Credit Card Expenses',
	'Credit Card Expenses',
]);

const isBenefitsAndTaxes = (description: string) =>
	description === 'Benefits & Taxes';

const isBankFees = (description: string) =>
	bankFeeDescriptions.has(description);

export const Route = createFileRoute(
	'/_claims/claim/$claimId/_claim/calculations/collectible',
)({
	component: function CollectibleCalculationsPage() {
		const { claimId } = Route.useParams();

		const [
			{ data: claim },
			{ data: collectible },
			{ data: countries },
			{ data: questionnaire },
		] = useSuspenseQueries({
			queries: [
				claimQueries.id(claimId),
				calculationQueries.collectible(claimId),
				countryQueries.list(),
				questionnaireQueries.id(claimId),
			],
		});

		const currency = useCurrency(claim.claimInsured.countryId);
		const isTierTwo = claim.isTier2Enabled && questionnaire.isTier2;
		const isTierTwoMonthly =
			isTierTwo && claim.estimatedPeriodofIndemnityDateType === 2;

		const countryName = useMemo(
			() => countries.find((c) => c.id === claim.claimInsured.countryId)?.name,
			[countries, claim.claimInsured.countryId],
		);

		const footnoteData = useMemo(() => {
			if (!isCalculatedCollectible(collectible)) {
				return {};
			}

			const benefitsFootnote =
				countryName === 'Canada' ?
					'Based on relationship of benefits and taxes to labour. All benefits and taxes are grouped as one expense and gets applied to uninsured payroll.'
				:	'Calculated based on response to Business Questionnaire. All benefits and taxes are grouped as one expense and gets applied to uninsured payroll.';

			const bankFeesFootnote = 'Assumes expense includes Merchant Fees.';

			return collectible.variableCosts.reduce<{
				bt?: FootnoteData;
				bf?: FootnoteData;
			}>((accumulator, variableCost) => {
				if (isBenefitsAndTaxes(variableCost.description)) {
					accumulator.bt = {
						letter: 'A',
						note: benefitsFootnote,
					};
				} else if (isBankFees(variableCost.description)) {
					accumulator.bf = {
						letter: 'B',
						note: bankFeesFootnote,
					};
				}

				return accumulator;
			}, {});
		}, [collectible, countryName]);

		if (!isCalculatedCollectible(collectible)) {
			return (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						p: 4,
						gap: 1,
						border: '1px solid',
						borderColor: 'accent.main',
						width: '600px',
						maxWidth: '100%',
						mx: 'auto',
					}}
				>
					<FontAwesomeIcon icon={faCalculatorSimple} size="2xl" />
					<Typography variant="subtitle1">No Calculations Data</Typography>
				</Box>
			);
		}

		return (
			<Box
				sx={{
					width: '600px',
					maxWidth: '100%',
					overflow: 'auto hidden',
				}}
			>
				<CalculationTable>
					<TableHead>
						<TableRow>
							<Cell header>Description</Cell>
							<Cell header nowrap sx={{ width: 0 }}>
								Percent
							</Cell>
							<Cell header>Amount</Cell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow>
							<Cell />
							<Cell />
							<Cell header small>
								({currency.display ?? currency.currency})
							</Cell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Annual Sales</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency} underline="double">
								{collectible.annualSales}
							</CurrencyCell>
						</TableRow>
						{isTierTwo && (
							<TableRow className="striped">
								<Cell>Sales Trend</Cell>
								<Cell percent align="center">
									{collectible.salesTrend}
								</Cell>
								<Cell />
							</TableRow>
						)}
						<TableRow className="striped">
							<Cell>Projected Sales</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency} underline>
								{collectible.projectedSales}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{collectible.variableCosts.length > 0 && (
							<>
								<TableRow>
									<Cell header colSpan={3} align="left">
										Less: Variable Costs
									</Cell>
								</TableRow>
								{collectible.variableCosts.map((variableCost, i, a) => (
									<TableRow key={variableCost.description} className="striped">
										<Cell indent>
											<Box sx={{ display: 'flex' }}>
												{variableCost.description}
												{isBenefitsAndTaxes(variableCost.description) ?
													<Box sx={{ marginLeft: 'auto' }}>
														<Footnote>A</Footnote>
													</Box>
												: isBankFees(variableCost.description) ?
													<Box sx={{ marginLeft: 'auto' }}>
														<Footnote>B</Footnote>
													</Box>
												:	null}
											</Box>
										</Cell>
										<Cell percent align="center">
											{variableCost.percent}
										</Cell>
										<CurrencyCell
											{...currency}
											hideSymbol
											underline={i === a.length - 1}
											noZero
										>
											{variableCost.amount}
										</CurrencyCell>
									</TableRow>
								))}
								<TableRow>
									<Cell spacer />
								</TableRow>
								<TableRow className="striped">
									<Cell>Total Variable Costs</Cell>
									<Cell />
									<CurrencyCell {...currency} hideSymbol>
										{collectible.totalVariableCosts}
									</CurrencyCell>
								</TableRow>
								<TableRow>
									<Cell spacer />
								</TableRow>
							</>
						)}
						<TableRow className="striped">
							<Cell>Projected Annual BI Value</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency} underline="double">
								{collectible.projectedAnnualBiValue}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						{collectible.maxIndemnityPeriodSales ?
							<>
								<TableRow className="striped">
									<Cell>Maximum Indemnity Period BI Value</Cell>
									<Cell />
									<CurrencyCell {...currency} hideSymbol>
										{collectible.maxIndemnityPeriodSales}
									</CurrencyCell>
								</TableRow>
								<TableRow>
									<Cell spacer />
								</TableRow>
							</>
						:	null}
						<TableRow className="striped">
							<Cell>Coinsurance Rate</Cell>
							<Cell percent align="right">
								{collectible.coinsuranceRate}
							</Cell>
							<Cell />
						</TableRow>
						<TableRow className="striped">
							<Cell>Insurance Required</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency}>
								{collectible.insuranceRequired}
							</CurrencyCell>
						</TableRow>
						<TableRow className="striped">
							<Cell>Insurance in Force</Cell>
							<Cell></Cell>
							<CurrencyCell {...currency}>
								{collectible.insuranceInForce}
							</CurrencyCell>
						</TableRow>
						<TableRow>
							<Cell spacer />
						</TableRow>
						<TableRow className="striped">
							<Cell>Collectible Rate</Cell>
							<Cell percent align="right" underline="double">
								{collectible.collectibleRate}
							</Cell>
							<Cell />
						</TableRow>
					</TableBody>
				</CalculationTable>
				<Box
					sx={{
						mt: 2,
					}}
				>
					<Typography
						variant="body2"
						sx={{
							padding: '6px 8px 6px 12px',
						}}
					>
						Notes
					</Typography>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'auto auto 1fr',
							columnGap: 1,
						}}
					>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'subgrid',
								gridColumn: '1 / -1',
								color: 'grey.700',
								p: '1px',
								'& .footnote': {
									padding: '6px 8px 6px 28px',
									display: 'grid',
									gridTemplateColumns: 'subgrid',
									gridColumn: '1 / -1',
									width: '100%',
									'& p': {
										display: 'flex',
										gap: 1,
									},
								},
							}}
							id={'footnote-extra'}
						>
							<Box className="footnote">
								<Typography variant="body2"></Typography>
								<Typography variant="body2">-</Typography>
								<Typography variant="body2">
									{isTierTwoMonthly ?
										'For coinsurance purposes, we have assumed sales trending for the sales loss calculation would be representative of the coinsurance requirements and, therefore, we have applied trending.'
									:	'For coinsurance purposes, we have assumed the uploaded financial statement is representative of the coinsurance requirements and, therefore, have not applied any trending.'
									}
								</Typography>
							</Box>
						</Box>
						{Object.values(footnoteData).map((f) => (
							<Box
								key={f.letter}
								sx={{
									display: 'grid',
									gridTemplateColumns: 'subgrid',
									gridColumn: '1 / -1',
									p: '1px',
									'& a.footnote': {
										padding: '6px 8px 6px 28px',
										display: 'grid',
										gridTemplateColumns: 'subgrid',
										gridColumn: '1 / -1',
										width: '100%',
										textDecoration: 'none',
										'&.active': {
											outline: '1px solid',
											outlineColor: 'accent.main',
											borderRadius: 1,
											backgroundColor: 'offwhite.light',
										},
										'& p': {
											display: 'flex',
											gap: 1,
										},
									},
								}}
								id={'footnote-' + f.letter}
							>
								<Footnote footnoteLetter={f.letter} className="footnote">
									<Typography variant="body2">{f.letter}</Typography>
									<Typography variant="body2">-</Typography>
									<Typography variant="body2">{f.note}</Typography>
								</Footnote>
							</Box>
						))}
					</Box>
				</Box>
			</Box>
		);
	},
});
